'use client';
import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import PipelineStatus from './PipelineStatus';

const STEPS = [
  { id: 'discovery', path: '/discovery' },
  { id: 'entry-point', path: '/entry-point' },
  { id: 'market-sizing', path: '/market-sizing' },
  { id: 'validation', path: '/validation' },
  { id: 'execution-plan', path: '/execution-plan' },
  { id: 'history', path: '/history' },
];

const STEP_ORDER = ['discovery', 'entry-point', 'market-sizing', 'validation', 'execution-plan', 'history'];

interface TopNavProps {
  onHistoryClick?: () => void;
  onSetupClick?: () => void;
}

export default function TopNav({ onHistoryClick, onSetupClick }: TopNavProps) {
  const pathname = usePathname();

  const currentIdx = STEP_ORDER.findIndex(s => {
    if (pathname === '/' && s === 'entry-point') return true;
    return pathname === `/${s}`;
  });

  return (
    <nav
      className="sticky top-0 z-50 w-full no-print"
      style={{
        borderBottom: '1px solid var(--border)',
        backgroundColor: 'rgba(6,6,8,0.92)',
        backdropFilter: 'blur(16px)',
      }}
    >
      <div
        className="mx-auto flex items-center justify-between px-4 sm:px-6"
        style={{ maxWidth: '1000px', paddingTop: '16px', paddingBottom: '14px' }}
      >
        {/* Left: Brand */}
        <Link href="/" className="flex items-baseline gap-1 no-underline">
          <span className="font-mono font-bold text-green text-[14px] tracking-wider">ORBITAL</span>
          <span className="font-mono text-text3 font-normal text-[14px] tracking-wider">NICHE</span>
        </Link>

        {/* Center: Step dots — hidden on mobile */}
        <div className="hidden sm:flex items-center gap-2">
          {STEPS.map((step, i) => {
            const isDone = i < currentIdx;
            const isActive = i === currentIdx;
            return (
              <Link key={`step-dot-${step.id}`} href={step.path === '/history' ? '/history' : step.path}>
                <span
                  className="block transition-all duration-200"
                  style={
                    isActive
                      ? { width: '24px', height: '8px', borderRadius: '4px', backgroundColor: 'var(--green)', boxShadow: '0 0 8px var(--green)' }
                      : isDone
                      ? { width: '8px', height: '8px', borderRadius: '50%', backgroundColor: 'rgba(57,255,110,0.5)' }
                      : { width: '8px', height: '8px', borderRadius: '50%', backgroundColor: 'var(--border2)' }
                  }
                />
              </Link>
            );
          })}
        </div>

        {/* Right: Icon buttons */}
        <div className="flex items-center gap-3">
          <PipelineStatus />
          <button
            onClick={onHistoryClick}
            title="History"
            className="flex items-center justify-center w-8 h-8 rounded-[6px] transition-all duration-150 hover:border-border2"
            style={{ border: '1px solid var(--border)', backgroundColor: 'transparent', color: 'var(--text2)' }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <rect x="2" y="2" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.2" />
              <rect x="9" y="2" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.2" />
              <rect x="2" y="9" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.2" />
              <rect x="9" y="9" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.2" />
            </svg>
          </button>
          <button
            onClick={onSetupClick}
            title="Setup"
            className="flex items-center justify-center w-8 h-8 rounded-[6px] transition-all duration-150 hover:border-border2"
            style={{ border: '1px solid var(--border)', backgroundColor: 'transparent', color: 'var(--text2)' }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="8" r="2.5" stroke="currentColor" strokeWidth="1.2" />
              <path d="M8 1v2M8 13v2M1 8h2M13 8h2M2.93 2.93l1.41 1.41M11.66 11.66l1.41 1.41M2.93 13.07l1.41-1.41M11.66 4.34l1.41-1.41" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      </div>
    </nav>
  );
}